import React, { useState } from "react";
import KpiCard from "./KpiCard";
import AlertRow from "./AlertRow";
import Chip from "./Chip";
import LiquidityChart from "./LiquidityChart";
import { dateLabel, money, percent } from "./money";

const labels = {
  operating_revenue: "Operating revenue",
  other_inflow: "Other cash in",
  sales_tax: "Sales tax",
  ap_operating: "AP – Operating",
  ap_inventory: "AP – Inventory",
  credit_card: "Credit card",
  payroll: "Payroll",
  owner_distribution: "Owner distributions",
  debt: "Debt repay",
  other_outflow: "Other cash out",
};
const inflows = ["operating_revenue", "other_inflow"];
const outflows = Object.keys(labels).filter((key) => !inflows.includes(key));
const varianceTone = (value) => {
  if (value == null) return "text-gray-500";
  return Number(value) >= 0 ? "text-emerald-700" : "text-red-700";
};
export const labelWidth = 180;
export const weekWidth = 92;
export const totalWidth = 120;
export const tableWidth = labelWidth + weekWidth * 13 + totalWidth;

function Row({
  label,
  values,
  weeks,
  hoveredWeek,
  tone = "text-gray-700",
  total,
  thresholdTone,
  totalTone,
  valueTone,
}) {
  return (
    <tr className="border-y border-transparent hover:border-gray-200">
      <td className="sticky left-0 z-20 bg-white px-3 py-2 font-medium text-gray-700">
        {label}
      </td>
      {weeks.map((week) => (
        <td
          key={week.weekIndex}
          className={`px-3 py-2 text-right tabular-nums ${hoveredWeek === week.weekIndex ? "bg-gray-100" : ""} ${thresholdTone ? thresholdTone(week) : valueTone ? valueTone(week) : tone}`}
        >
          {money(values(week))}
        </td>
      ))}
      <td
        className={`sticky right-0 z-20 bg-gray-50 px-3 py-2 text-right font-medium tabular-nums ${totalTone || "text-gray-900"}`}
      >
        {money(total)}
      </td>
    </tr>
  );
}

function negativeTone(value) {
  return Number(value) < 0 ? "text-red-700" : "text-gray-900";
}

function endingCashTone(value, thresholds) {
  if (Number(value) < Number(thresholds?.grossCashFloor))
    return "font-semibold text-red-700";
  if (Number(value) < Number(thresholds?.cautionThreshold))
    return "font-semibold text-amber-700";
  return "font-semibold text-gray-900";
}

function formatDateKey(value) {
  if (!value) return "—";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  return `${month}/${day}/${year}`;
}

function formatForecastDate(value) {
  const dateKey = String(value || "").slice(0, 10);
  const [year, month, day] = dateKey.split("-").map(Number);
  if (!year || !month || !day) return formatDateKey(value);
  const weekday = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][
    new Date(Date.UTC(year, month - 1, day)).getUTCDay()
  ];
  return `${weekday} ${formatDateKey(dateKey)}`;
}

export default function SummaryTab({
  summary,
  loading,
  error,
  history,
  historyLoading,
  historyError,
  selectedWeek,
  onSelectWeek,
  settings,
  onToggleSetting,
}) {
  const [chartOpen, setChartOpen] = useState(true);
  const [hoveredWeek, setHoveredWeek] = useState(null);
  const [selectedPriorWeekStart, setSelectedPriorWeekStart] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const weeks = summary?.weekly || [];
  const priorWeeks = (history?.priorWeeks || [])
    .slice()
    .sort((left, right) => right.weekStart.localeCompare(left.weekStart))
    .slice(0, 5);
  const priorWeek = priorWeeks.find(
    (item) => item.weekStart === selectedPriorWeekStart,
  ) || priorWeeks[0];
  const settingValues = settings?.settings || settings || {};
  const apInventoryAuto = settingValues.apInventory?.mode === "po_auto";
  const hiddenCategories = new Set(
    settings?.settings?.display?.hiddenCategories ||
      settings?.display?.hiddenCategories ||
      [],
  );
  const varianceCategories = Object.keys(labels).filter(
    (category) => !hiddenCategories.has(category),
  );
  const missingScheduleItems = summary?.alerts?.find(
    (alert) => alert.code === "missing_schedule_amount",
  )?.meta?.items || [];
  const coverageIncomplete = summary?.coverageIncomplete || {
    count: missingScheduleItems.length,
    names: missingScheduleItems
      .map((item) => (typeof item === "string" ? item : item?.name))
      .filter(Boolean),
  };

  if (!summary && loading)
    return (
      <div className="flex min-h-[420px] items-center justify-center text-sm text-gray-500">
        Loading summary…
      </div>
    );
  if (error && !summary)
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-sm text-red-700">
        {error}
      </div>
    );
  if (!summary)
    return (
      <div className="rounded-lg border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-600">
        No summary is available for this cash-flow horizon.
      </div>
    );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 rounded-lg border border-gray-200 bg-white sm:grid-cols-3 lg:grid-cols-6">
        <KpiCard
          label="Current week ending cash"
          value={summary.kpis?.currentWeekEndingCash}
          moneyValue
        />
        <KpiCard
          label="Avg weekly net cash flow"
          value={summary.kpis?.avgWeeklyNetCashFlow}
          moneyValue
        />
        <KpiCard
          label="Projected minimum cash"
          value={summary.kpis?.minProjectedCash}
          caption={`Week ${Number(summary.kpis?.minWeekIndex || 0) + 1}`}
          moneyValue
          tone="text-orange-700"
        />
        <KpiCard
          label="Weeks below floor"
          value={summary.kpis?.weeksBelowFloor ?? "—"}
          caption={`Floor ${money(summary.thresholds?.grossCashFloor)}`}
        />
        <KpiCard
          label="Final week ending cash"
          value={summary.kpis?.finalWeekEndingCash}
          moneyValue
        />
        <KpiCard
          label="Effective Capital"
          value={summary.effectiveCapital?.kingsLoot}
          caption={`Floor ${money(summary.effectiveCapital?.floor)}`}
          moneyValue
          tone={
            Number(summary.effectiveCapital?.kingsLoot) <
            Number(summary.effectiveCapital?.floor)
              ? "text-red-700"
              : "text-emerald-700"
          }
        />
      </div>
      <AlertRow alerts={summary.alerts} />
      <div className="flex items-center justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-lg font-semibold text-gray-900">
              Weekly liquidity
            </p>
            {coverageIncomplete?.count > 0 && (
              <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                Forecast incomplete: no amount set for {coverageIncomplete.names.join(", ")}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500">
            {dateLabel(summary.window.start)} – {dateLabel(summary.window.end)}{" "}
            · {summary.window.timezone}
          </p>
          <p className="text-xs text-gray-500">
            Opening cash:{" "}
            {summary.metadata?.openingCashSourceLabel ||
              (summary.openingCash?.source === "effective_capital"
                ? "Effective Capital · Kings Loot cash"
                : "Plaid depository accounts")}
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
          <input
            type="checkbox"
            checked={apInventoryAuto}
            disabled={!onToggleSetting}
            onChange={(event) =>
              onToggleSetting?.("apInventory", {
                ...(settingValues.apInventory || {}),
                mode: event.target.checked ? "po_auto" : "manual",
              })
            }
          />
          Auto-schedule inventory AP from PO due dates
        </label>
        <button
          type="button"
          onClick={() => setChartOpen((open) => !open)}
          className="text-xs font-medium text-gray-600 hover:text-gray-900"
        >
          {chartOpen ? "Hide chart" : "Show chart"}
        </button>
      </div>
      <div className="max-h-[70vh] overflow-auto rounded-lg border border-gray-200 bg-white">
        <div
          className="sticky top-0 z-30 grid bg-white text-[11px] font-semibold text-gray-800"
          style={{
            gridTemplateColumns: `${labelWidth}px repeat(13, ${weekWidth}px) ${totalWidth}px`,
            width: `${tableWidth}px`,
          }}
        >
          <div className="sticky left-0 z-40 bg-white px-3 py-2">
            Weekly statement
          </div>
          {weeks.map((week) => (
            <button
              type="button"
              key={week.weekIndex}
              onClick={() => onSelectWeek(week.weekIndex)}
              onMouseEnter={() => setHoveredWeek(week.weekIndex)}
              onMouseLeave={() => setHoveredWeek(null)}
              className={`px-3 py-2 text-right ${week.weekIndex === selectedWeek ? "bg-gray-100" : hoveredWeek === week.weekIndex ? "bg-gray-100" : ""}`}
            >
              {`Wk ${week.weekIndex + 1} · ${dateLabel(week.start)}`}
              <span className="mt-1 block">
                <Chip tone={week.state}>{week.state}</Chip>
              </span>
            </button>
          ))}
          <div className="bg-gray-50 px-3 py-2 text-right">Total</div>
        </div>
        {chartOpen && (
          <LiquidityChart
            weekly={weeks}
            floor={summary.thresholds?.grossCashFloor}
            caution={summary.thresholds?.cautionThreshold}
            selectedWeek={selectedWeek}
            onSelect={onSelectWeek}
            hoveredWeek={hoveredWeek}
            onHoverWeek={setHoveredWeek}
            tableWidth={tableWidth}
            labelWidth={labelWidth}
            weekWidth={weekWidth}
          />
        )}
        <table
          className="table-fixed text-sm"
          style={{ width: `${tableWidth}px` }}
        >
          <colgroup>
            <col style={{ width: `${labelWidth}px` }} />
            {weeks.map((week) => (
              <col key={week.weekIndex} style={{ width: `${weekWidth}px` }} />
            ))}
            <col style={{ width: `${totalWidth}px` }} />
          </colgroup>
          <tbody>
            <Row
              label="Beginning cash"
              values={(week) => week.beginningCash}
              weeks={weeks}
              hoveredWeek={hoveredWeek}
              total={weeks[0]?.beginningCash}
              valueTone={(week) => negativeTone(week.beginningCash)}
              totalTone={negativeTone(weeks[0]?.beginningCash)}
            />
            {inflows
              .filter((category) => !hiddenCategories.has(category))
              .map((category) => (
                <Row
                  key={category}
                  label={labels[category]}
                  values={(week) => week.values?.[category]}
                  weeks={weeks}
                  hoveredWeek={hoveredWeek}
                  tone="text-emerald-700"
                  total={weeks.reduce(
                    (sum, week) => sum + Number(week.values?.[category] || 0),
                    0,
                  )}
                />
              ))}
            <Row
              label="Total cash in"
              values={(week) => week.totalCashIn}
              weeks={weeks}
              hoveredWeek={hoveredWeek}
              tone="font-semibold text-emerald-700"
              total={weeks.reduce(
                (sum, week) => sum + Number(week.totalCashIn || 0),
                0,
              )}
            />
            {outflows
              .filter((category) => !hiddenCategories.has(category))
              .map((category) => (
                <Row
                  key={category}
                  label={labels[category]}
                  values={(week) => week.values?.[category]}
                  weeks={weeks}
                  hoveredWeek={hoveredWeek}
                  tone="text-red-700"
                  total={weeks.reduce(
                    (sum, week) => sum + Number(week.values?.[category] || 0),
                    0,
                  )}
                />
              ))}
            <Row
              label="Total cash out"
              values={(week) => week.totalCashOut}
              weeks={weeks}
              hoveredWeek={hoveredWeek}
              tone="font-semibold text-red-700"
              total={weeks.reduce(
                (sum, week) => sum + Number(week.totalCashOut || 0),
                0,
              )}
            />
            <Row
              label="Net cash flow"
              values={(week) => week.netCashFlow}
              weeks={weeks}
              hoveredWeek={hoveredWeek}
              tone="font-semibold text-gray-900"
              valueTone={(week) => negativeTone(week.netCashFlow)}
              total={weeks.reduce(
                (sum, week) => sum + Number(week.netCashFlow || 0),
                0,
              )}
              totalTone={negativeTone(
                weeks.reduce(
                  (sum, week) => sum + Number(week.netCashFlow || 0),
                  0,
                ),
              )}
            />
            <Row
              label="Ending cash"
              values={(week) => week.endingCash}
              weeks={weeks}
              hoveredWeek={hoveredWeek}
              thresholdTone={(week) =>
                endingCashTone(week.endingCash, summary.thresholds)
              }
              total={summary.kpis?.finalWeekEndingCash}
              totalTone={endingCashTone(
                summary.kpis?.finalWeekEndingCash,
                summary.thresholds,
              )}
            />
            <tr className="border-t border-gray-200 text-xs text-gray-400">
              <td className="sticky left-0 bg-white px-3 py-2">
                Gross-cash floor
              </td>
              {weeks.map((week) => (
                <td
                  key={week.weekIndex}
                  className={`px-3 py-2 text-right tabular-nums ${hoveredWeek === week.weekIndex ? "bg-gray-100" : ""}`}
                >
                  {money(summary.thresholds?.grossCashFloor)}
                </td>
              ))}
              <td className="sticky right-0 bg-gray-50" />
            </tr>
            <tr className="text-xs text-gray-400">
              <td className="sticky left-0 bg-white px-3 py-2">Caution</td>
              {weeks.map((week) => (
                <td
                  key={week.weekIndex}
                  className={`px-3 py-2 text-right tabular-nums ${hoveredWeek === week.weekIndex ? "bg-gray-100" : ""}`}
                >
                  {money(summary.thresholds?.cautionThreshold)}
                </td>
              ))}
              <td className="sticky right-0 bg-gray-50" />
            </tr>
          </tbody>
        </table>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-3">
        <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-gray-800">
              Variance · Week of {formatDateKey(priorWeek?.weekStart)}
            </p>
            <p className="mt-1 text-xs text-gray-500">
              {historyLoading
                ? "Loading prior weeks…"
                : priorWeek?.week
                  ? `Forecast saved ${formatDateKey(priorWeek.generatedAt)}${
                    priorWeek.offsetWeeks > 0
                      ? ` (${priorWeek.offsetWeeks} ${priorWeek.offsetWeeks === 1 ? "week" : "weeks"} ahead)`
                      : ""
                  }`
                  : priorWeek?.unavailableReason ||
                    "No saved forecast exists for this week."}
            </p>
            {priorWeek?.week && priorWeek.skipped?.length > 0 && (
              <p className="mt-1 text-xs text-gray-500">
                {priorWeek.skipped
                  .map((item) => `The ${formatDateKey(item.weekStart)} forecast is excluded (${item.reason}).`)
                  .join(" ")}
              </p>
            )}
            {historyError && (
              <p className="mt-1 text-xs text-red-700">{historyError}</p>
            )}
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-500">
            <span className="sr-only">Select a prior week</span>
            <select
              aria-label="Select a prior week"
              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700"
              disabled={historyLoading || priorWeeks.length === 0}
              value={priorWeek?.weekStart || ""}
              onChange={(event) => {
                setSelectedPriorWeekStart(event.target.value);
                setOpenDetail(null);
              }}
            >
              {priorWeeks.length === 0 ? (
                <option value="">
                  {historyLoading
                    ? "Loading prior weeks…"
                    : "No completed weeks available"}
                </option>
              ) : (
                priorWeeks.map((item) => (
                  <option key={item.weekStart} value={item.weekStart}>
                    Week of {formatDateKey(item.weekStart)}
                  </option>
                ))
              )}
            </select>
          </label>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-[600px] w-full text-sm">
            <thead className="text-left text-[11px] uppercase tracking-widest text-gray-400">
              <tr>
                <th className="px-2 py-2">Category</th>
                <th className="px-2 py-2 text-right">Actual</th>
                <th className="px-2 py-2 text-right">Forecast</th>
                <th className="px-2 py-2 text-right">Var $</th>
                <th className="px-2 py-2 text-right">Var %</th>
              </tr>
            </thead>
            <tbody>
              {varianceCategories.map((category) => {
                const direction = inflows.includes(category) ? 1 : -1;
                const actualValue = priorWeek?.actual?.values?.[category];
                const forecastValue = priorWeek?.week?.values?.[category];
                const actual = actualValue == null ? null : direction * Number(actualValue);
                const forecast = forecastValue == null ? null : direction * Number(forecastValue);
                const dollar = actual == null || forecast == null
                  ? null
                  : Number(actual) - Number(forecast);
                const pct = dollar == null || Number(forecast) === 0
                  ? null
                  : dollar / Math.abs(Number(forecast));
                const tone = varianceTone(dollar);
                const isOpen = openDetail?.category === category;
                const detailId = `prior-week-detail-${category}`;
                const actualLines = priorWeek?.actualLines?.[category] || [];
                const savedDays = priorWeek?.daily || [];
                const forecastLines = savedDays
                  .map((day) => ({
                    date: day.date,
                    amount: Number(day.values?.[category] || 0),
                  }))
                  .filter((line) => line.amount !== 0);
                const revenueByChannel = category === "operating_revenue"
                  ? priorWeek?.revenueByChannel
                  : null;
                const toggleDetail = (type) => {
                  setOpenDetail((current) => (
                    current?.category === category && current?.type === type
                      ? null
                      : { category, type }
                  ));
                };

                return (
                  <React.Fragment key={category}>
                    <tr className="border-t border-gray-100">
                      <td className="px-2 py-2">
                        {labels[category] || category}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        <button
                          aria-controls={detailId}
                          aria-expanded={openDetail?.category === category && openDetail?.type === "actual"}
                          aria-label={`Show actual details for ${labels[category] || category}`}
                          className={`cursor-pointer ${negativeTone(actual)} underline-offset-2 hover:underline focus:underline`}
                          type="button"
                          onClick={() => toggleDetail("actual")}
                        >
                          {actual == null ? "—" : money(actual)}
                        </button>
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums">
                        <button
                          aria-controls={detailId}
                          aria-expanded={openDetail?.category === category && openDetail?.type === "forecast"}
                          aria-label={`Show forecast details for ${labels[category] || category}`}
                          className={`cursor-pointer ${negativeTone(forecast)} underline-offset-2 hover:underline focus:underline`}
                          type="button"
                          onClick={() => toggleDetail("forecast")}
                        >
                          {forecast == null ? "—" : money(forecast)}
                        </button>
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${tone}`}>
                        {dollar == null ? "—" : money(dollar, { signed: true })}
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${tone}`}>
                        {pct == null ? "—" : percent(pct)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr id={detailId} className="border-t border-gray-100 bg-gray-50">
                        <td className="px-2 py-3" colSpan={5}>
                          <div className="grid gap-4 md:grid-cols-2">
                            <div>
                              <p className="mb-2 text-xs font-semibold text-gray-700">Actual</p>
                              {actualLines.length > 0 ? (
                                <ul className="space-y-1 text-xs text-gray-600">
                                  {actualLines.map((line, index) => (
                                    <li key={`${line.label}-${line.date}-${index}`}>
                                      {line.label} · {formatDateKey(line.date)} · {money(line.amount)}
                                    </li>
                                  ))}
                                </ul>
                              ) : (
                                <p className="text-xs text-gray-500">No bank activity</p>
                              )}
                            </div>
                            <div>
                              <p className="mb-2 text-xs font-semibold text-gray-700">Forecast</p>
                              {priorWeek?.week && savedDays.length > 0 ? (
                                forecastLines.length > 0 ? (
                                  <ul className="space-y-1 text-xs text-gray-600">
                                    {forecastLines.map((line) => (
                                      <li key={line.date}>
                                        {formatForecastDate(line.date)} · {money(line.amount)}
                                      </li>
                                    ))}
                                  </ul>
                                ) : (
                                  <p className="text-xs text-gray-500">No forecast activity</p>
                                )
                              ) : priorWeek?.week ? (
                                <p className="text-xs text-gray-500">
                                  Daily detail wasn't saved with this forecast (weekly total only).
                                </p>
                              ) : (
                                <p className="text-xs text-gray-500">
                                  No saved forecast is available.
                                </p>
                              )}
                              {revenueByChannel && (
                                <div className="mt-2 text-xs text-gray-600">
                                  <p className="font-semibold text-gray-700">Revenue by channel</p>
                                  {[
                                    ["Shopify", revenueByChannel.shopify],
                                    ["Amazon", revenueByChannel.amazon],
                                    ["Other", revenueByChannel.other],
                                  ].map(([channel, amount]) => (
                                    <p key={channel}>
                                      {channel} · {money(amount)}
                                    </p>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
              {(() => {
                const actualIn = priorWeek?.actual?.totalCashIn;
                const forecastIn = priorWeek?.week?.totalCashIn;
                const actualOut = priorWeek?.actual?.totalCashOut;
                const forecastOut = priorWeek?.week?.totalCashOut;
                const rows = [
                  { label: "Total cash in", actual: actualIn, forecast: forecastIn, inflow: true },
                  { label: "Total cash out", actual: actualOut == null ? null : -Number(actualOut), forecast: forecastOut == null ? null : -Number(forecastOut), inflow: false },
                  {
                    label: "Total Cash In/(Out)",
                    actual: actualIn == null || actualOut == null
                      ? null
                      : Number(actualIn) - Number(actualOut),
                    forecast: forecastIn == null || forecastOut == null
                      ? null
                      : Number(forecastIn) - Number(forecastOut),
                    inflow: true,
                  },
                ];
                return rows.map((row) => {
                  const dollar = row.actual == null || row.forecast == null
                    ? null
                    : Number(row.actual) - Number(row.forecast);
                  const pct = dollar == null || Number(row.forecast) === 0
                    ? null
                    : dollar / Math.abs(Number(row.forecast));
                  const tone = varianceTone(dollar);
                  return (
                    <tr key={row.label} className="border-t border-gray-200 font-semibold">
                      <td className="px-2 py-2">{row.label}</td>
                      <td className={`px-2 py-2 text-right tabular-nums ${negativeTone(row.actual)}`}>
                        {row.actual == null ? "—" : money(row.actual)}
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${negativeTone(row.forecast)}`}>
                        {row.forecast == null ? "—" : money(row.forecast)}
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${tone}`}>
                        {dollar == null ? "—" : money(dollar, { signed: true })}
                      </td>
                      <td className={`px-2 py-2 text-right tabular-nums ${tone}`}>
                        {pct == null ? "—" : percent(pct)}
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>
      {error && <p className="text-xs text-red-700">{error}</p>}
    </div>
  );
}
